import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Button, Tree, TreeItem, TreeItemContent } from 'react-aria-components';
import type { Key } from '@react-types/shared';
import Splitter from '../shared/Splitter';
import { DebouncedFilterInput } from '../shared/DebouncedFilterInput';
import { RefreshIcon } from '../../shared/icons';
import apiClient from '../../shared/api/apiClient';
import { hasCapability } from '../../utils/accessControl';
import './styles.css';
import type {
    ClientCategoryDto,
    CategoryData,
    ProductsProps,
    SupplierProductDto,
    ProductsResponse,
    PaginationInfo,
    SortField,
    SortDirection
} from './types';
import type { TreeNode } from '../shared/TreeControl/types';
import { formatAvailableDate } from './formatAvailableDate';
import { useBusinessTimeZone } from '../../hooks/useBusinessTimeZone';

const ABYSS_CATEGORY_ID = -1;
const PRODUCT_LEVEL_SCOPE = 2;

const AttributeFilterMode = {
    ValueList: 1,
    NumericRange: 2,
    BooleanCheckbox: 3
} as const;

type AttributeFilterMode = typeof AttributeFilterMode[keyof typeof AttributeFilterMode];

interface ProductAttributeFilterDto {
    attributeDefinitionId: number;
    attributeCode: string;
    attributeName: string;
    displayName: string;
    filterMode: AttributeFilterMode;
    values: string[];
    min?: number;
    max?: number;
}

interface ProductAttributeFiltersResponse {
    filters: ProductAttributeFilterDto[];
}

type AttributeFilterState = Record<string, string | string[] | { min: string; max: string }>;
type ProductDetailTab = 'suppliers' | 'attributes';

interface MainProductAttributeValueDto {
    attributeDefinitionId: number;
    attributeCode: string;
    attributeName: string;
    displayName: string;
    groupName?: string;
    dataTypeName: string;
    unitName?: string;
    unitSymbol?: string;
    value?: string;
    isFilterable: boolean;
    isExportedToB2B: boolean;
    updatedAt: string;
}

interface MainProductAttributesResponse {
    attributes: MainProductAttributeValueDto[];
}

interface BarcodeMergeExclusionDto {
    integrationId: number;
    scope: number;
    productExternalId?: string;
    barcode: string;
}

interface SkuMergeExclusionDto {
    integrationId: number;
    scope: number;
    productExternalId?: string;
    sku: string;
}

const convertCategoryToTreeNode = (category: ClientCategoryDto): TreeNode<CategoryData> => {
    return {
        id: category.id,
        name: category.name,
        children: [],
        data: {
            createdAt: category.createdAt,
            updatedAt: category.updatedAt,
            createdById: category.createdById,
            updatedById: category.updatedById
        },
        parentId: category.parentCategoryId
    };
};

const buildCategoryTree = (categories: ClientCategoryDto[]): TreeNode<CategoryData>[] => {
    const categoryMap = new Map<number, TreeNode<CategoryData>>();
    const rootCategories: TreeNode<CategoryData>[] = [];

    categories.forEach(cat => {
        const treeNode = convertCategoryToTreeNode(cat);
        categoryMap.set(cat.id, treeNode);
    });

    categories.forEach(cat => {
        const category = categoryMap.get(cat.id)!;
        if (cat.parentCategoryId) {
            const parent = categoryMap.get(cat.parentCategoryId);
            if (parent) {
                parent.children.push(category);
            }
        } else {
            rootCategories.push(category);
        }
    });

    return rootCategories;
};

const formatMarkupText = (pricePercent?: number, priceSum?: number) => {
    const parts: string[] = [];

    if (pricePercent !== undefined && pricePercent !== null && pricePercent !== 0) {
        parts.push(`${pricePercent > 0 ? '+' : ''}${pricePercent}%`);
    }

    if (priceSum !== undefined && priceSum !== null && priceSum !== 0) {
        parts.push(`${priceSum > 0 ? '+' : ''}${priceSum.toLocaleString('ru-RU')}`);
    }

    return parts.join(' ');
};

const renderPriceValue = (price?: number | null) => {
    if (price === undefined || price === null) {
        return '-';
    }

    return Math.round(price).toLocaleString('ru-RU');
};

const renderSourcePriceCell = (product: SupplierProductDto) => {
    return renderPriceValue(product.originalPrice ?? product.price);
};

const renderMarkupPriceCell = (
    price?: number | null,
    pricePercent?: number,
    priceSum?: number,
    categoryPricePercent?: number,
    categoryPriceSum?: number
) => {
    if (price === undefined || price === null) {
        return '-';
    }

    const markupText = formatMarkupText(pricePercent, priceSum);
    const categoryMarkupText = formatMarkupText(categoryPricePercent, categoryPriceSum);

    return (
        <div>
            <span>{renderPriceValue(price)}</span>
            {markupText ? (
                <span className="price-markup-note">
                    {markupText}
                </span>
            ) : null}
            {categoryMarkupText ? (
                <span className="price-category-markup-note">
                    {categoryMarkupText}
                </span>
            ) : null}
        </div>
    );
};

const renderSimplePriceCell = (price?: number | null) => {
    if (price === undefined || price === null) {
        return '-';
    }

    return renderPriceValue(price);
};

interface ProductCategoryTreeProps {
    categories: TreeNode<CategoryData>[];
    selectedCategoryId?: number;
    onCategorySelected: (node?: TreeNode<CategoryData>) => void;
}

const ProductCategoryTree: React.FC<ProductCategoryTreeProps> = ({
    categories,
    selectedCategoryId,
    onCategorySelected
}) => {
    const [expandedKeys, setExpandedKeys] = useState<Set<Key>>(() => {
        try {
            const saved = localStorage.getItem('productManagement_categoryTreeExpanded');
            const ids = saved ? JSON.parse(saved) : [];
            return Array.isArray(ids) ? new Set(ids) : new Set();
        } catch (error) {
            console.warn('Failed to load product category tree expansion state:', error);
            return new Set();
        }
    });

    const nodeById = useMemo(() => {
        const map = new Map<number, TreeNode<CategoryData>>();
        const visit = (nodes: TreeNode<CategoryData>[]) => {
            for (const node of nodes) {
                map.set(node.id, node);
                visit(node.children);
            }
        };

        visit(categories);
        return map;
    }, [categories]);

    useEffect(() => {
        try {
            localStorage.setItem('productManagement_categoryTreeExpanded', JSON.stringify(Array.from(expandedKeys)));
        } catch (error) {
            console.warn('Failed to save product category tree expansion state:', error);
        }
    }, [expandedKeys]);

    const handleSelectionChange = useCallback((keys: 'all' | Set<Key>) => {
        if (keys === 'all') {
            return;
        }

        const [selectedKey] = Array.from(keys);
        const selectedNode = selectedKey === undefined ? undefined : nodeById.get(Number(selectedKey));
        onCategorySelected(selectedNode);
    }, [nodeById, onCategorySelected]);

    const toggleCategoryExpansion = useCallback((nodeId: number) => {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(nodeId)) {
                next.delete(nodeId);
            } else {
                next.add(nodeId);
            }

            return next;
        });
    }, []);

    const renderCategory = (node: TreeNode<CategoryData>): React.ReactNode => {
        const hasChildren = node.children.length > 0;

        return (
            <TreeItem
                key={node.id}
                id={node.id}
                textValue={node.name}
                value={node}
                className="product-category-tree-item"
            >
                <TreeItemContent>
                    {({ isExpanded, hasChildItems }) => (
                        <div
                            className="product-category-tree-row"
                            onClick={(event) => {
                                if (!hasChildren || (event.target instanceof Element && event.target.closest('button'))) {
                                    return;
                                }

                                toggleCategoryExpansion(node.id);
                            }}
                        >
                            {hasChildItems ? (
                                <Button slot="chevron" className="product-category-tree-toggle">
                                    <span className={`product-category-tree-chevron ${isExpanded ? 'expanded' : ''}`}>
                                        {'>'}
                                    </span>
                                </Button>
                            ) : (
                                <span className="product-category-tree-toggle placeholder" />
                            )}

                            <span className="product-category-tree-name">{node.name}</span>
                        </div>
                    )}
                </TreeItemContent>

                {hasChildren && node.children.map(child => renderCategory(child))}
            </TreeItem>
        );
    };

    return (
        <Tree
            aria-label="Категории продуктов"
            className="product-category-tree"
            expandedKeys={expandedKeys}
            onExpandedChange={setExpandedKeys}
            selectedKeys={selectedCategoryId === undefined ? [] : [selectedCategoryId]}
            onSelectionChange={handleSelectionChange}
            selectionMode="single"
            selectionBehavior="toggle"
        >
            {categories.map(category => renderCategory(category))}
        </Tree>
    );
};

const ProductManagement: React.FC<ProductsProps> = ({ currentUser }) => {
    const businessTimeZone = useBusinessTimeZone();
    const canAssignMainProductCategory = hasCapability(currentUser, 'categories.edit');
    const canEditProductRules = hasCapability(currentUser, 'exclusions.edit');
    const [categories, setCategories] = useState<TreeNode<CategoryData>[]>([]);
    const [loading, setLoading] = useState(true);
    const [leftPanelWidth, setLeftPanelWidth] = useState(300);
    const [isResizing, setIsResizing] = useState(false);
    const [topPanelHeight, setTopPanelHeight] = useState(() => {
        const saved = localStorage.getItem('productManagement_topPanelHeight');
        return saved ? parseInt(saved) : 400;
    });
    const [isResizingVertical, setIsResizingVertical] = useState(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);
    
    const [searchString, setSearchString] = useState<string | undefined>(undefined);
    const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);

    const [products, setProducts] = useState<SupplierProductDto[]>([]);
    const [pagination, setPagination] = useState<PaginationInfo | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(() => {
        const saved = localStorage.getItem('productManagement_pageSize');
        return saved ? parseInt(saved) : 100;
    });
    const [sortField, setSortField] = useState<SortField | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    // Toggle visibility of the inline column filter row
    const [showInlineFilters, setShowInlineFilters] = useState(() => {
        const saved = localStorage.getItem('productManagement_showInlineFilters');
        return saved === null ? false : saved === 'true';
    });
    // Toggle visibility of the attribute filter controls (the in-table column filters always stay)
    const [showAttributeFilters, setShowAttributeFilters] = useState(() => {
        const saved = localStorage.getItem('productManagement_showAttributeFilters');
        return saved === null ? true : saved === 'true';
    });
    // Show only products that have availability (delivery date), price and stock
    const [onlyAvailable, setOnlyAvailable] = useState(() => {
        return localStorage.getItem('productManagement_onlyAvailable') === 'true';
    });

    // Column filters
    const [filters, setFilters] = useState({
        id: '',
        code: '',
        vendor: '',
        name: '',
        sku: '',
        barcode: ''
    });
    const [attributeFilters, setAttributeFilters] = useState<ProductAttributeFilterDto[]>([]);
    const [activeAttributeFilters, setActiveAttributeFilters] = useState<AttributeFilterState>({});

    // Selected products for batch operations
    const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(new Set());
    const [showCategoryMoveModal, setShowCategoryMoveModal] = useState(false);
    const [targetCategoryId, setTargetCategoryId] = useState<number | undefined>(undefined);
    const [movingProducts, setMovingProducts] = useState(false);
    const [categorySearchFilter, setCategorySearchFilter] = useState('');

    // Toggle product selection for batch operations
    const handleToggleProductSelection = useCallback((productId: number) => {
        setSelectedProductIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(productId)) {
                newSet.delete(productId);
            } else {
                newSet.add(productId);
            }
            return newSet;
        });
    }, []);

    // Select all products on current page
    const handleSelectAll = useCallback(() => {
        if (selectedProductIds.size === products.length) {
            setSelectedProductIds(new Set());
        } else {
            setSelectedProductIds(new Set(products.map(p => p.id)));
        }
    }, [products, selectedProductIds.size]);

    // Move selected products to category
    const handleMoveToCategory = useCallback(async () => {
        if (selectedProductIds.size === 0 || !targetCategoryId && targetCategoryId !== ABYSS_CATEGORY_ID) {
            return;
        }

        setMovingProducts(true);
        try {
            const categoryId = targetCategoryId === ABYSS_CATEGORY_ID ? null : targetCategoryId;
            await apiClient.post('api/products/assign-category', {
                productIds: Array.from(selectedProductIds),
                categoryId: categoryId
            });

            setSelectedProductIds(new Set());
            setShowCategoryMoveModal(false);
            setTargetCategoryId(undefined);
            setCategorySearchFilter('');
            // Reload will be called after the state updates
            setTimeout(() => window.location.reload(), 100);
            alert(`Успешно перемещено ${selectedProductIds.size} продуктов`);
        } catch (error) {
            console.error('Error moving products:', error);
            alert('Ошибка при перемещении продуктов');
        } finally {
            setMovingProducts(false);
        }
    }, [selectedProductIds, targetCategoryId]);

    // Flatten categories for dropdown
    const flattenCategories = useCallback((nodes: TreeNode<CategoryData>[], depth = 0): { id: number; name: string; depth: number; hasChildren: boolean }[] => {
        const result: { id: number; name: string; depth: number; hasChildren: boolean }[] = [];
        for (const node of nodes) {
            result.push({ id: node.id, name: node.name, depth, hasChildren: node.children.length > 0 });
            if (node.children.length > 0) {
                result.push(...flattenCategories(node.children, depth + 1));
            }
        }
        return result;
    }, []);

    const flatCategories = flattenCategories(categories);

    const filteredCategories = useMemo(() => {
        const query = categorySearchFilter.trim().toLowerCase();
        if (!query) return flatCategories;
        const matchingIds = new Set(
            flatCategories.filter(c => c.name.toLowerCase().includes(query)).map(c => c.id)
        );
        const parentChain = new Set<number>();
        for (const cat of flatCategories) {
            if (matchingIds.has(cat.id)) {
                let depth = cat.depth;
                for (let i = flatCategories.indexOf(cat) - 1; i >= 0 && depth > 0; i--) {
                    if (flatCategories[i].depth < depth) {
                        parentChain.add(flatCategories[i].id);
                        depth = flatCategories[i].depth;
                    }
                }
            }
        }
        return flatCategories.filter(c => matchingIds.has(c.id) || parentChain.has(c.id));
    }, [flatCategories, categorySearchFilter]);

    // Selected product and related products
    const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
    const [selectedProductCategoryId, setSelectedProductCategoryId] = useState<number | null>(null);
    const [selectedProductBarcode, setSelectedProductBarcode] = useState<string>('');
    const [relatedProducts, setRelatedProducts] = useState<SupplierProductDto[]>([]);
    const [excludedBarcodesByProduct, setExcludedBarcodesByProduct] = useState<Record<string, Set<string>>>({});
    const [excludedSkusByProduct, setExcludedSkusByProduct] = useState<Record<string, Set<string>>>({});
    const [loadingRelated, setLoadingRelated] = useState(false);
    const [relatedProductsLoadedKey, setRelatedProductsLoadedKey] = useState('');
    const [relatedSortField] = useState<SortField | null>('price');
    const [relatedSortDirection] = useState<SortDirection>('asc');
    const [activeDetailTab, setActiveDetailTab] = useState<ProductDetailTab>('suppliers');
    const [mainProductAttributes, setMainProductAttributes] = useState<MainProductAttributeValueDto[]>([]);
    const [attributesLoadedKey, setAttributesLoadedKey] = useState('');
    const [loadingAttributes, setLoadingAttributes] = useState(false);
    const [attributesError, setAttributesError] = useState<string | null>(null);


    const containerRef = useRef<HTMLDivElement>(null);
    const selectedProductIdRef = useRef<number | null>(null);
    // Cancels the in-flight products request so a stale slow response can't
    // overwrite the results of a newer category/filter/page request.
    const productsRequestRef = useRef<AbortController | null>(null);

    useEffect(() => {
        selectedProductIdRef.current = selectedProductId;
    }, [selectedProductId]);

    // Abort any in-flight products request on unmount.
    useEffect(() => () => productsRequestRef.current?.abort(), []);

    const loadCategories = useCallback(async () => {
        try {
            setLoading(true);

            const response = await apiClient.get<ClientCategoryDto[]>(
                'api/client-categories');

            let treeData = buildCategoryTree(response.data || []);

            if (treeData.length) {
                treeData = treeData[0].children;
            }

            const specialAbyssCategory: TreeNode<CategoryData> = {
                id: ABYSS_CATEGORY_ID,
                name: 'Бездна',
                children: []
            };

            setCategories([specialAbyssCategory, ...treeData]);  

        } catch (error) {
            console.error('Ошибка при загрузке категорий:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadAttributeFilters = useCallback(async (categoryId?: number) => {
        if (!categoryId || categoryId === ABYSS_CATEGORY_ID) {
            setAttributeFilters([]);
            setActiveAttributeFilters({});
            return;
        }

        try {
            const response = await apiClient.get<ProductAttributeFiltersResponse>(
                `api/products/attribute-filters?clientCategoryId=${categoryId}`);
            setAttributeFilters(response.data.filters ?? []);
            setActiveAttributeFilters({});
        } catch (error) {
            console.error('Error loading product attribute filters:', error);
            setAttributeFilters([]);
            setActiveAttributeFilters({});
        }
    }, []);

    useEffect(() => {
        void loadAttributeFilters(selectedCategoryId);
    }, [loadAttributeFilters, selectedCategoryId]);

    const loadProducts = useCallback(async (page = currentPage, size = pageSize) => {
        // Cancel a previous in-flight request; the newest call always wins.
        productsRequestRef.current?.abort();
        const controller = new AbortController();
        productsRequestRef.current = controller;
        const { signal } = controller;

        setLoading(true);
        setProducts([]);
        setPagination(null);

        // Add filter parameters
        const params = new URLSearchParams({
            page: page.toString(),
            pageSize: size.toString()
        });

        if (searchString?.trim())
        {
            params.append("search", searchString);
        }

        if (selectedCategoryId === ABYSS_CATEGORY_ID)
        {
            params.append('withoutCategory', 'true');
        }
        else if (selectedCategoryId)
        {
            params.append("clientCategoryId", selectedCategoryId.toString());
        }

        // Add sorting parameters
        if (sortField) {
            params.append('sortBy', sortField);
            params.append('sortDescending', (sortDirection === 'desc').toString());
        }

        // Add column filter parameters
        if (filters.code.trim()) {
            params.append('filterCode', filters.code);
        }
        if (filters.vendor.trim()) {
            params.append('filterVendor', filters.vendor);
        }
        if (filters.name.trim()) {
            params.append('filterName', filters.name);
        }
        if (filters.sku.trim()) {
            params.append('filterSku', filters.sku);
        }
        if (filters.barcode.trim()) {
            params.append('filterBarcode', filters.barcode);
        }
        if (onlyAvailable) {
            params.append('onlyAvailable', 'true');
        }

        attributeFilters.forEach(filter => {
            const key = filter.attributeDefinitionId.toString();
            const value = activeAttributeFilters[key];
            if (!value) {
                return;
            }

            if (filter.filterMode === AttributeFilterMode.NumericRange && typeof value === 'object' && !Array.isArray(value)) {
                if (value.min.trim()) {
                    params.append(`attributeMin_${key}`, value.min.trim());
                }

                if (value.max.trim()) {
                    params.append(`attributeMax_${key}`, value.max.trim());
                }
            } else if (filter.filterMode === AttributeFilterMode.BooleanCheckbox && typeof value === 'string' && value) {
                params.append(`attributeBool_${key}`, value);
            } else if (Array.isArray(value) && value.length > 0) {
                params.append(`attributeValue_${key}`, value.join('|'));
            }
        });

        try {
            const response = await apiClient.get<ProductsResponse>(
                `api/products?${params}`, {}, true, signal);

            // A newer request superseded this one while it was in flight.
            if (signal.aborted) {
                return;
            }

            const products = response.data.products || [];
            const currentSelectedProductId = selectedProductIdRef.current;
            if (currentSelectedProductId && !products.find(p => p.id === currentSelectedProductId)) {
                setSelectedProductId(null);
                setSelectedProductCategoryId(null);
            }

            setProducts(products);
            setPagination(response.data.pagination);
            setCurrentPage(response.data.pagination.page);
        } catch (error) {
            // Request was cancelled (newer request started / component unmounted):
            // leave state alone so the winning request owns it.
            if (signal.aborted || (error instanceof Error && error.name === 'AbortError')) {
                return;
            }
            console.error('Error loading products:', error);
            setSelectedProductId(null);
            setSelectedProductCategoryId(null);
            setProducts([]);
            setPagination(null);
        }
        setLoading(false);
    }, [selectedCategoryId, sortField, sortDirection, searchString, filters, onlyAvailable, attributeFilters, activeAttributeFilters, currentPage, pageSize]);

    // Effect to reload products when filters change
    useEffect(() => {
        if (selectedCategoryId || searchString?.trim() || filters.code || filters.vendor || filters.name || filters.sku || filters.barcode || onlyAvailable || Object.keys(activeAttributeFilters).length > 0) {
            setCurrentPage(1); // Reset to first page when filters change
            loadProducts(1, pageSize);
        } else {
            setProducts([]);
            setPagination(null);
        }
    }, [selectedCategoryId, pageSize, searchString, filters, onlyAvailable, activeAttributeFilters, loadProducts]);

    // Handle sorting by column click
    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Handle key press for filters
    const handleFilterKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            setSearchString(tempSearchString);
        }
    }, [tempSearchString]);

    const handleCategorySelect = useCallback((node?: TreeNode | undefined) => {
        if (selectedCategoryId === node?.id) {
            setSelectedCategoryId(undefined);
        } else {
            setSelectedCategoryId(node?.id);
        }
    }, [selectedCategoryId]);

    // Handle page change
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        loadProducts(page, pageSize);
    };

    // Handle page size change
    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1); // Reset to first page
        loadProducts(1, size);
    };

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    // Save page size to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('productManagement_pageSize', pageSize.toString());
    }, [pageSize]);

    // Save top panel height to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('productManagement_topPanelHeight', topPanelHeight.toString());
    }, [topPanelHeight]);


    // Load related products
    const loadRelatedProducts = useCallback(async (productId: number, categoryId: number | null = null, force = false) => {
        const loadKey = `${productId}:${categoryId ?? ''}:${relatedSortField ?? ''}:${relatedSortDirection}`;
        if (!force && relatedProductsLoadedKey === loadKey) {
            return;
        }

        setLoadingRelated(true);
        setRelatedProducts([]);

        const params = new URLSearchParams();
        if (categoryId !== null) {
            params.append('categoryId', categoryId.toString());
        }

        if (relatedSortField) {
            params.append('sortBy', relatedSortField);
            params.append('sortDescending', (relatedSortDirection === 'desc').toString());
        }

        if (onlyAvailable) {
            params.append('onlyAvailable', 'true');
        }

        try {
            const response = await apiClient.get<SupplierProductDto[]>(
                `api/products/${productId}/related?${params}`);
            setRelatedProducts(response.data || []);
            setRelatedProductsLoadedKey(loadKey);
        } catch (error) {
            console.error('Error loading related products:', error);
            setRelatedProducts([]);
            setRelatedProductsLoadedKey('');
        } finally {
            setLoadingRelated(false);
        }
    }, [relatedProductsLoadedKey, relatedSortField, relatedSortDirection, onlyAvailable]);

    const loadMainProductAttributes = useCallback(async (productId: number, categoryId: number | null = null, force = false) => {
        const loadKey = `${productId}:${categoryId ?? ''}`;
        if (!force && attributesLoadedKey === loadKey) {
            return;
        }

        setLoadingAttributes(true);
        setAttributesError(null);

        try {
            const params = new URLSearchParams();
            if (categoryId !== null) {
                params.append('categoryId', categoryId.toString());
            }

            const response = await apiClient.get<MainProductAttributesResponse>(
                `api/products/${productId}/attributes${params.toString() ? '?' + params.toString() : ''}`);

            setMainProductAttributes(response.data.attributes ?? []);
            setAttributesLoadedKey(loadKey);
        } catch (error) {
            console.error('Error loading main product attributes:', error);
            setMainProductAttributes([]);
            setAttributesError('Не удалось загрузить атрибуты');
            setAttributesLoadedKey('');
        } finally {
            setLoadingAttributes(false);
        }
    }, [attributesLoadedKey]);

    const getProductKey = useCallback((integrationId?: number, productExternalId?: string) => {
        if (!integrationId || !productExternalId) {
            return '';
        }

        return `${integrationId}:${productExternalId}`;
    }, []);

    const loadBarcodeExclusionsForRelatedProducts = useCallback(async (productsToCheck: SupplierProductDto[]) => {
        const uniquePairs = new Map<string, { integrationId: number; productExternalId: string }>();
        for (const product of productsToCheck) {
            if (!product.integrationId || !product.productExternalId) {
                continue;
            }

            const key = getProductKey(product.integrationId, product.productExternalId);
            if (!uniquePairs.has(key)) {
                uniquePairs.set(key, {
                    integrationId: product.integrationId,
                    productExternalId: product.productExternalId
                });
            }
        }

        if (uniquePairs.size === 0) {
            setExcludedBarcodesByProduct({});
            return;
        }

        try {
            const results = await Promise.all(
                Array.from(uniquePairs.values()).map(async ({ integrationId, productExternalId }) => {
                    const response = await apiClient.get<BarcodeMergeExclusionDto[]>(
                        `api/supplier-products/barcode-exclusions?integrationId=${integrationId}&productExternalId=${encodeURIComponent(productExternalId)}`
                    );

                    return {
                        key: getProductKey(integrationId, productExternalId),
                        exclusions: response.data || []
                    };
                })
            );

            const map: Record<string, Set<string>> = {};
            for (const result of results) {
                map[result.key] = new Set(
                    result.exclusions
                        .filter(x => x.scope === PRODUCT_LEVEL_SCOPE)
                        .map(x => x.barcode)
                );
            }

            setExcludedBarcodesByProduct(map);
        } catch (error) {
            console.error('Error loading barcode exclusions:', error);
            setExcludedBarcodesByProduct({});
        }
    }, [getProductKey]);

    const loadSkuExclusionsForRelatedProducts = useCallback(async (productsToCheck: SupplierProductDto[]) => {
        const uniquePairs = new Map<string, { integrationId: number; productExternalId: string }>();
        for (const product of productsToCheck) {
            if (!product.integrationId || !product.productExternalId) {
                continue;
            }

            const key = getProductKey(product.integrationId, product.productExternalId);
            if (!uniquePairs.has(key)) {
                uniquePairs.set(key, {
                    integrationId: product.integrationId,
                    productExternalId: product.productExternalId
                });
            }
        }

        if (uniquePairs.size === 0) {
            setExcludedSkusByProduct({});
            return;
        }

        try {
            const results = await Promise.all(
                Array.from(uniquePairs.values()).map(async ({ integrationId, productExternalId }) => {
                    const response = await apiClient.get<SkuMergeExclusionDto[]>(
                        `api/supplier-products/sku-exclusions?integrationId=${integrationId}&productExternalId=${encodeURIComponent(productExternalId)}`
                    );

                    return {
                        key: getProductKey(integrationId, productExternalId),
                        exclusions: response.data || []
                    };
                })
            );

            const map: Record<string, Set<string>> = {};
            for (const result of results) {
                map[result.key] = new Set(
                    result.exclusions
                        .filter(x => x.scope === PRODUCT_LEVEL_SCOPE)
                        .map(x => x.sku)
                );
            }

            setExcludedSkusByProduct(map);
        } catch (error) {
            console.error('Error loading sku exclusions:', error);
            setExcludedSkusByProduct({});
        }
    }, [getProductKey]);

    const handleToggleBarcodeExclusion = useCallback(async (product: SupplierProductDto, barcode: string) => {
        if (!product.integrationId || !product.productExternalId || !barcode) {
            return;
        }

        const key = getProductKey(product.integrationId, product.productExternalId);
        const isExcluded = excludedBarcodesByProduct[key]?.has(barcode) ?? false;

        try {
            if (isExcluded) {
                await apiClient.delete(
                    `api/supplier-products/barcode-exclusions?integrationId=${product.integrationId}&scope=${PRODUCT_LEVEL_SCOPE}&barcode=${encodeURIComponent(barcode)}&productExternalId=${encodeURIComponent(product.productExternalId)}`
                );
            } else {
                await apiClient.post('api/supplier-products/barcode-exclusions', {
                    integrationId: product.integrationId,
                    scope: PRODUCT_LEVEL_SCOPE,
                    productExternalId: product.productExternalId,
                    barcode: barcode,
                    reason: 'Manual toggle from related products UI'
                });
            }

            setExcludedBarcodesByProduct(prev => {
                const current = new Set(prev[key] ?? []);
                if (isExcluded) {
                    current.delete(barcode);
                } else {
                    current.add(barcode);
                }

                return {
                    ...prev,
                    [key]: current
                };
            });
        } catch (error) {
            console.error('Error toggling barcode exclusion:', error);
            alert('Ошибка при изменении исключения штрихкода');
        }
    }, [excludedBarcodesByProduct, getProductKey]);

    const handleToggleSkuExclusion = useCallback(async (product: SupplierProductDto, sku: string) => {
        if (!product.integrationId || !product.productExternalId || !sku.trim()) {
            return;
        }

        const normalizedSku = sku.trim();
        const key = getProductKey(product.integrationId, product.productExternalId);
        const isExcluded = excludedSkusByProduct[key]?.has(normalizedSku) ?? false;

        try {
            if (isExcluded) {
                await apiClient.delete(
                    `api/supplier-products/sku-exclusions?integrationId=${product.integrationId}&scope=${PRODUCT_LEVEL_SCOPE}&sku=${encodeURIComponent(normalizedSku)}&productExternalId=${encodeURIComponent(product.productExternalId)}`
                );
            } else {
                await apiClient.post('api/supplier-products/sku-exclusions', {
                    integrationId: product.integrationId,
                    scope: PRODUCT_LEVEL_SCOPE,
                    productExternalId: product.productExternalId,
                    sku: normalizedSku,
                    reason: 'Manual toggle from related products UI'
                });
            }

            setExcludedSkusByProduct(prev => {
                const current = new Set(prev[key] ?? []);
                if (isExcluded) {
                    current.delete(normalizedSku);
                } else {
                    current.add(normalizedSku);
                }

                return {
                    ...prev,
                    [key]: current
                };
            });
        } catch (error) {
            console.error('Error toggling sku exclusion:', error);
            alert('Ошибка при изменении исключения SKU');
        }
    }, [excludedSkusByProduct, getProductKey]);

    const getRelatedCategoryId = useCallback((product: SupplierProductDto) => {
        if (product.clientCategoryId && product.clientCategoryId > 0) {
            return product.clientCategoryId;
        }

        if (selectedCategoryId && selectedCategoryId !== ABYSS_CATEGORY_ID) {
            return selectedCategoryId;
        }

        return null;
    }, [selectedCategoryId]);

    // Handle product selection
    const handleProductSelect = useCallback((product: SupplierProductDto) => {
        if (selectedProductId === product.id) {
            setSelectedProductId(null);
            setSelectedProductCategoryId(null);
            setRelatedProducts([]);
            setRelatedProductsLoadedKey('');
            setMainProductAttributes([]);
            setAttributesLoadedKey('');
        } else {
            const categoryId = getRelatedCategoryId(product);
            setSelectedProductId(product.id);
            setSelectedProductCategoryId(categoryId);
            setSelectedProductBarcode(product.barcode ?? '');
            setRelatedProducts([]);
            setRelatedProductsLoadedKey('');
            setMainProductAttributes([]);
            setAttributesLoadedKey('');
        }
    }, [selectedProductId, getRelatedCategoryId]);

    // Effect to lazily load related products when the suppliers tab is active
    useEffect(() => {
        if (selectedProductId && activeDetailTab === 'suppliers') {
            loadRelatedProducts(selectedProductId, selectedProductCategoryId);
        }
    }, [activeDetailTab, relatedSortField, relatedSortDirection, selectedProductId, selectedProductCategoryId, loadRelatedProducts, onlyAvailable]);

    // Effect to lazily load attributes when the attributes tab is active
    useEffect(() => {
        if (selectedProductId && activeDetailTab === 'attributes') {
            loadMainProductAttributes(selectedProductId, selectedProductCategoryId);
        }
    }, [activeDetailTab, selectedProductId, selectedProductCategoryId, loadMainProductAttributes]);

    useEffect(() => {
        if (!selectedProductId || relatedProducts.length === 0) {
            setExcludedBarcodesByProduct({});
            setExcludedSkusByProduct({});
            return;
        }

        loadBarcodeExclusionsForRelatedProducts(relatedProducts);
        loadSkuExclusionsForRelatedProducts(relatedProducts);
    }, [selectedProductId, relatedProducts, loadBarcodeExclusionsForRelatedProducts, loadSkuExclusionsForRelatedProducts]);

    // Handle export to Excel
    const handleExport = useCallback(async () => {
        try {
            const params = new URLSearchParams();

            if (searchString?.trim()) {
                params.append("search", searchString);
            }

            if (selectedCategoryId === ABYSS_CATEGORY_ID) {
                params.append('withoutCategory', 'true');
            } else if (selectedCategoryId) {
                params.append("clientCategoryId", selectedCategoryId.toString());
            }

            // Add sorting parameters
            if (sortField) {
                params.append('sortBy', sortField);
                params.append('sortDescending', (sortDirection === 'desc').toString());
            }

            const url = `api/products/export${params.toString() ? '?' + params.toString() : ''}`;

            // Use fetch directly for blob response
            const response = await fetch(url, {
                method: 'GET'
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            // Create download link
            const blob = await response.blob();
            const urlObj = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = urlObj;
            link.download = `products_export_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(urlObj);
        } catch (error) {
            console.error('Error exporting products:', error);
            alert('Ошибка при экспорте данных');
        }
    }, [searchString, selectedCategoryId, sortField, sortDirection]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isResizing || !containerRef.current) return;

        const newWidth = e.clientX - containerRef.current.getBoundingClientRect().left;
        // Constrain width between min and max values
        const constrainedWidth = Math.max(200, Math.min(600, newWidth));
        setLeftPanelWidth(constrainedWidth);
    }, [isResizing]);

    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
    }, []);

    const handleMouseDownVertical = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizingVertical(true);
    }, []);

    const handleMouseMoveVertical = useCallback((e: MouseEvent) => {
        if (!isResizingVertical || !containerRef.current) return;

        const rightPanelContent = containerRef.current.querySelector('.right-panel-content') as HTMLElement;
        if (!rightPanelContent) return;

        const rect = rightPanelContent.getBoundingClientRect();
        const minPanelHeight = 180;
        const splitterHeight = 4;
        const newHeight = e.clientY - rect.top;
        const maxTopHeight = Math.max(minPanelHeight, rect.height - splitterHeight - minPanelHeight);
        const constrainedHeight = Math.max(minPanelHeight, Math.min(maxTopHeight, newHeight));
        setTopPanelHeight(constrainedHeight);
    }, [isResizingVertical]);

    const handleMouseUpVertical = useCallback(() => {
        setIsResizingVertical(false);
    }, []);

    // Add event listeners for resizing
    useEffect(() => {
        if (isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

    // Add event listeners for vertical resizing
    useEffect(() => {
        if (isResizingVertical) {
            window.addEventListener('mousemove', handleMouseMoveVertical);
            window.addEventListener('mouseup', handleMouseUpVertical);
        } else {
            window.removeEventListener('mousemove', handleMouseMoveVertical);
            window.removeEventListener('mouseup', handleMouseUpVertical);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMoveVertical);
            window.removeEventListener('mouseup', handleMouseUpVertical);
        };
    }, [isResizingVertical, handleMouseMoveVertical, handleMouseUpVertical]);

    useEffect(() => {
        if (!selectedProductId || !containerRef.current) return;

        const rightPanelContent = containerRef.current.querySelector('.right-panel-content') as HTMLElement;
        if (!rightPanelContent) return;

        const minPanelHeight = 180;
        const splitterHeight = 4;
        const maxTopHeight = Math.max(minPanelHeight, rightPanelContent.clientHeight - splitterHeight - minPanelHeight);

        if (topPanelHeight > maxTopHeight) {
            setTopPanelHeight(maxTopHeight);
        }
    }, [selectedProductId, topPanelHeight]);

    const formatAttributeUpdatedAt = (updatedAt: string) => {
        if (!updatedAt) {
            return '-';
        }

        const date = new Date(updatedAt);
        if (Number.isNaN(date.getTime())) {
            return '-';
        }

        return date.toLocaleDateString('ru-RU');
    };

    const renderAttributeFlag = (enabled: boolean) => (
        <span className={`attribute-flag ${enabled ? 'enabled' : ''}`}>
            {enabled ? 'Да' : 'Нет'}
        </span>
    );

    const renderAttributeFilterControl = (filter: ProductAttributeFilterDto) => {
        const key = filter.attributeDefinitionId.toString();
        const value = activeAttributeFilters[key];

        if (filter.filterMode === AttributeFilterMode.NumericRange) {
            const rangeValue = typeof value === 'object' && !Array.isArray(value)
                ? value
                : { min: '', max: '' };

            return (
                <div className="attribute-filter-range">
                    <input
                        type="number"
                        className="filter-input"
                        placeholder={filter.min !== undefined && filter.min !== null ? filter.min.toString() : 'от'}
                        value={rangeValue.min}
                        onChange={(event) => setActiveAttributeFilters(current => ({
                            ...current,
                            [key]: { ...rangeValue, min: event.target.value }
                        }))}
                    />
                    <input
                        type="number"
                        className="filter-input"
                        placeholder={filter.max !== undefined && filter.max !== null ? filter.max.toString() : 'до'}
                        value={rangeValue.max}
                        onChange={(event) => setActiveAttributeFilters(current => ({
                            ...current,
                            [key]: { ...rangeValue, max: event.target.value }
                        }))}
                    />
                </div>
            );
        }

        if (filter.filterMode === AttributeFilterMode.BooleanCheckbox) {
            return (
                <select
                    className="filter-input"
                    value={typeof value === 'string' ? value : ''}
                    onChange={(event) => setActiveAttributeFilters(current => ({
                        ...current,
                        [key]: event.target.value
                    }))}
                >
                    <option value="">Любое</option>
                    <option value="true">Да</option>
                    <option value="false">Нет</option>
                </select>
            );
        }

        const selectedValues = Array.isArray(value) ? value : [];

        return (
            <select
                className="filter-input"
                value={selectedValues[0] ?? ''}
                onChange={(event) => setActiveAttributeFilters(current => ({
                    ...current,
                    [key]: event.target.value ? [event.target.value] : []
                }))}
            >
                <option value="">Любое</option>
                {filter.values.map(option => (
                    <option key={option} value={option}>{option}</option>
                ))}
            </select>
        );
    };

    return (
        <>
        <div className="category-management container-fluid" ref={containerRef}>
            <div className="filter-controls-compact">
                <div className="filter-controls-row">
                    <h3>Продукты</h3>
                    {/* <div ref={dropdownRef} className="category-dropdown">
                        <button
                            className='btn-compact primary'
                            onClick={handleCategoryButtonClick}
                        >
                            {selectedCategoryName ?? 'Категории'}
                        </button>
                    </div> */}
                    <input
                        type="text"
                        value={tempSearchString}
                        onChange={(e) => setTempSearchString(e.target.value)}
                        onKeyDown={handleFilterKeyDown}
                        className="search-input-fullwidth"
                        placeholder="Поиск по продуктам..."
                    />
                    <button
                        className={`btn-compact ${showInlineFilters ? 'primary' : 'refresh'}`}
                        onClick={() => setShowInlineFilters(prev => {
                            const next = !prev;
                            localStorage.setItem('productManagement_showInlineFilters', next.toString());
                            return next;
                        })}
                        aria-pressed={showInlineFilters}
                        title={showInlineFilters ? 'Скрыть фильтры' : 'Показать фильтры'}
                    >
                        🔍
                    </button>
                    <button className="btn-compact refresh" onClick={() => loadProducts(currentPage, pageSize)} disabled={loading} title="Обновить">
                        {loading ? (
                            <div className="btn-spinner" />
                        ) : (
                            <RefreshIcon />
                        )}
                    </button>
                    <button className="btn-compact refresh" onClick={handleExport} title="Экспорт в Excel">
                        📈
                    </button>
                    <button
                        className={`btn-compact ${onlyAvailable ? 'primary' : 'refresh'}`}
                        onClick={() => setOnlyAvailable(prev => {
                            const next = !prev;
                            localStorage.setItem('productManagement_onlyAvailable', next.toString());
                            return next;
                        })}
                        aria-pressed={onlyAvailable}
                        title="Доступность"
                    >
                        ✅
                    </button>
                    {attributeFilters.length > 0 && (
                        <button
                            className="btn-compact refresh"
                            onClick={() => setShowAttributeFilters(prev => {
                                const next = !prev;
                                localStorage.setItem('productManagement_showAttributeFilters', next.toString());
                                return next;
                            })}
                            aria-pressed={!showAttributeFilters}
                            title={showAttributeFilters ? 'Скрыть фильтры' : 'Показать фильтры'}
                        >
                            {showAttributeFilters ? '🔽' : '🔼'}
                        </button>
                    )}
                    {canAssignMainProductCategory && selectedProductIds.size > 0 && (
                        <button 
                            className="btn-compact primary" 
                            onClick={() => setShowCategoryMoveModal(true)}
                            style={{ marginLeft: '0.5rem' }}
                        >
                            Переместить ({selectedProductIds.size})
                        </button>
                    )}
                </div>
            </div>
            <div className="new-categories-container">
                <div className="split-view">
                    <div className="left-panel" style={{ width: leftPanelWidth }}>
                        <div className="tree-container">
                            <ProductCategoryTree
                                categories={categories}
                                selectedCategoryId={selectedCategoryId}
                                onCategorySelected={handleCategorySelect}
                            />
                        </div>
                    </div>

                    {/* Resize Handle */}
                    <Splitter
                        direction="horizontal"
                        onMouseDown={handleMouseDown}
                        isResizing={isResizing}
                    />

                    {/* Right Panel - Content Sections */}
                    <div className="right-panel">
                        <div className="right-panel-content">

                            {/* Products Section */}
                            <div
                                className="products-section"
                                style={selectedProductId && !loading
                                    ? { height: topPanelHeight, flex: '0 0 auto', minHeight: 180 }
                                    : { flex: '1 1 auto', minHeight: 0 }}
                            >
                                <div className="products-container">
                                    {showAttributeFilters && attributeFilters.length > 0 && (
                                        <div className="product-attribute-filters">
                                            {attributeFilters.map(filter => (
                                                <label key={filter.attributeDefinitionId} className="product-attribute-filter">
                                                    <span>{filter.displayName}</span>
                                                    {renderAttributeFilterControl(filter)}
                                                </label>
                                            ))}
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => setActiveAttributeFilters({})}
                                                type="button"
                                            >
                                                Сбросить
                                            </button>
                                        </div>
                                    )}
                                    <div className="table-responsive">
                                        <div className="products-table-container">
                                            <table className="products-table">
                                                <thead className="products-table-header-sticky">
                                                    <tr>
                                                        {canAssignMainProductCategory && (
                                                            <th className="selection-column">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedProductIds.size === products.length && products.length > 0}
                                                                    onChange={handleSelectAll}
                                                                    title={selectedProductIds.size === products.length ? "Снять выделение" : "Выбрать все"}
                                                                />
                                                            </th>
                                                        )}
                                                        <th
                                                            onClick={() => handleSort('code')}
                                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                                            title="Сортировать по Коду"
                                                        >
                                                            <div className="stacked-header">
                                                                <span>Код {sortField === 'code' && (sortDirection === 'asc' ? '↑' : '↓')}</span>
                                                                <span className="stacked-header-sub">Вендор {sortField === 'vendor' && (sortDirection === 'asc' ? '↑' : '↓')}</span>
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() => handleSort('name')}
                                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                                            title="Сортировать по Названию"
                                                        >
                                                            Название {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                                                        </th>
                                                        <th
                                                            onClick={() => handleSort('sku')}
                                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                                            title="Сортировать по SKU"
                                                        >
                                                            <div className="stacked-header">
                                                                <span>SKU {sortField === 'sku' && (sortDirection === 'asc' ? '↑' : '↓')}</span>
                                                                <span className="stacked-header-sub">Баркоды</span>
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() => handleSort('price')}
                                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                                            title="Сортировать по Цене 1"
                                                        >
                                                            <div className="stacked-header">
                                                                <span>Цена 1, ₽ {sortField === 'price' && (sortDirection === 'asc' ? '↑' : '↓')}</span>
                                                                <span className="stacked-header-sub">Цена 2, ₽</span>
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() => handleSort('stock')}
                                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                                            title="Сортировать по Наличию"
                                                        >
                                                            <div className="stacked-header">
                                                                <span>Наличие {sortField === 'stock' && (sortDirection === 'asc' ? '↑' : '↓')}</span>
                                                                <span className="stacked-header-sub">Доступно</span>
                                                            </div>
                                                        </th>
                                                    </tr>
                                                    {showInlineFilters && (
                                                    <tr className="filter-row">
                                                        {canAssignMainProductCategory && (
                                                            <th className="selection-column" aria-hidden="true" />
                                                        )}
                                                        <th>
                                                            <DebouncedFilterInput
                                                                value={filters.code}
                                                                onChange={(value) => setFilters(prev => ({ ...prev, code: value }))}
                                                                placeholder="Код"
                                                            />
                                                        </th>
                                                        <th>
                                                            <DebouncedFilterInput
                                                                value={filters.name}
                                                                onChange={(value) => setFilters(prev => ({ ...prev, name: value }))}
                                                                placeholder="Название"
                                                            />
                                                        </th>
                                                        <th>
                                                            <DebouncedFilterInput
                                                                value={filters.sku}
                                                                onChange={(value) => setFilters(prev => ({ ...prev, sku: value }))}
                                                                placeholder="SKU"
                                                            />
                                                        </th>
                                                        <th>
                                                            <input type="number" className="filter-input" placeholder="Цена 1, ₽" disabled />
                                                        </th>
                                                        <th>
                                                            <input type="number" className="filter-input" placeholder="Дата" disabled />
                                                        </th>
                                                    </tr>
                                                    )}
                                                </thead>
                                                <tbody className="products-table-body">
                                                    {products.map((product) => (
                                                        <tr
                                                            key={`${product.id}`}
                                                            onClick={() => handleProductSelect(product)}
                                                            className={`${selectedProductId === product.id ? 'selected-product-row' : ''} ${product.isActive === false ? 'inactive-product-row' : ''}`}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            {canAssignMainProductCategory && (
                                                                <td className="selection-column" onClick={(e) => e.stopPropagation()}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedProductIds.has(product.id)}
                                                                        onChange={() => handleToggleProductSelection(product.id)}
                                                                    />
                                                                </td>
                                                            )}
                                                            <td>
                                                                <div className="stacked-cell">
                                                                    <span>{product.code ?? '-'}</span>
                                                                    <span className="stacked-cell-sub">{product.vendor || '-'}</span>
                                                                </div>
                                                            </td>
                                                            <td title={product.name}>{product.name}</td>
                                                            <td>
                                                                <div className="stacked-cell">
                                                                    <span>{product.sku || '-'}</span>
                                                                    <span className="stacked-cell-sub">{product.barcode}</span>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="stacked-cell">
                                                                    <span>{renderSimplePriceCell(product.price)}</span>
                                                                    <span className="stacked-cell-sub">{renderSimplePriceCell(product.price2)}</span>
                                                                </div>
                                                            </td>
                                                            <td className="availability-cell">
                                                                <div className="stacked-cell">
                                                                    <span>{product.stock || '-'}</span>
                                                                    <span className="stacked-cell-sub">
                                                                        {product.hasRelatedProducts && product.availableDate ? (
                                                                            <>
                                                                                {(() => { const { date, time } = formatAvailableDate(product.availableDate, businessTimeZone); return `${date} ${time}`; })()}
                                                                            </>
                                                                        ) : '-'}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            {loading
                                                ? <h3 className='ml-2'>Загрузка...</h3>
                                                : !!products.length || <h3 className='ml-2'>Товаров не найдено.</h3> 
                                            }
                                        </div>
                                    </div>

                                    {/* Pagination Controls */}
                                    {pagination && pagination.totalPages > 1 && (
                                        <div className="pagination-container" style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center', gap: '1rem' }}>
                                            <div className="pagination-info">
                                                Всего: {pagination.totalCount} | Страница {pagination.page} из {pagination.totalPages}
                                            </div>

                                            <div className="page-size-selector">
                                                <label htmlFor="pageSize">Размер страницы:</label>
                                                <select
                                                    id="pageSize"
                                                    value={pageSize}
                                                    onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                                                    style={{ marginLeft: '0.5rem', padding: '0.25rem' }}
                                                >
                                                    <option value={10}>10</option>
                                                    <option value={20}>20</option>
                                                    <option value={50}>50</option>
                                                    <option value={100}>100</option>
                                                    <option value={250}>250</option>
                                                    <option value={500}>500</option>
                                                    <option value={1000}>1000</option>
                                                </select>
                                            </div>

                                            <div className="pagination-buttons">
                                                <button
                                                    onClick={() => handlePageChange(pagination.page - 1)}
                                                    disabled={pagination.page <= 1}
                                                    style={{ padding: '0.25rem 0.5rem', marginRight: '0.25rem' }}
                                                >
                                                    ‹‹
                                                </button>

                                                {(() => {
                                                    const pages = [];
                                                    const startPage = Math.max(1, pagination.page - 2);
                                                    const endPage = Math.min(pagination.totalPages, pagination.page + 2);

                                                    for (let i = startPage; i <= endPage; i++) {
                                                        pages.push(
                                                            <button
                                                                key={i}
                                                                onClick={() => handlePageChange(i)}
                                                                disabled={i === pagination.page}
                                                                style={{
                                                                    padding: '0.25rem 0.5rem',
                                                                    marginRight: '0.25rem',
                                                                    fontWeight: i === pagination.page ? 'bold' : 'normal'
                                                                }}
                                                            >
                                                                {i}
                                                            </button>
                                                        );
                                                    }

                                                    return pages;
                                                })()}

                                                <button
                                                    onClick={() => handlePageChange(pagination.page + 1)}
                                                    disabled={pagination.page >= pagination.totalPages}
                                                    style={{ padding: '0.25rem 0.5rem' }}
                                                >
                                                    ››
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Vertical Resize Handle */}
                            {selectedProductId && !loading && (
                                <Splitter
                                    direction="vertical"
                                    onMouseDown={handleMouseDownVertical}
                                    isResizing={isResizingVertical}
                                />
                            )}

                            {/* Related Products Section */}
                            {selectedProductId && !loading && (
                                <div className="related-products-section product-details-section">
                                    <div className="product-detail-tabs">
                                        <button
                                            className={activeDetailTab === 'suppliers' ? 'active' : ''}
                                            onClick={() => setActiveDetailTab('suppliers')}
                                            type="button"
                                        >
                                            Поставщики
                                        </button>
                                        <button
                                            className={activeDetailTab === 'attributes' ? 'active' : ''}
                                            onClick={() => setActiveDetailTab('attributes')}
                                            type="button"
                                        >
                                            Атрибуты
                                        </button>
                                    </div>
                                    <div className="products-container">
                                    <div className="table-responsive" style={{ display: activeDetailTab === 'suppliers' ? undefined : 'none' }}>
                                        {loadingRelated ? (
                                            <div style={{ textAlign: 'center', padding: '2rem' }}>
                                                <div className="btn-spinner" />
                                                <p style={{ marginTop: '0.5rem', color: '#6c757d' }}>Загрузка...</p>
                                            </div>
                                        ) : relatedProducts.length === 0 ? (
                                            <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                                                Связанных продуктов не найдено
                                            </div>
                                        ) : (
                                            <>
                                            <div className="related-products-rows">
                                                {relatedProducts.map((product) => (
                                                    <div key={`${product.id}`} className="related-product-row">
                                                        <div className="related-product-row-header">
                                                            <span>
                                                                <span
                                                                    className="related-product-row-id"
                                                                    title="Нажмите чтобы скопировать ID"
                                                                    onClick={() => navigator.clipboard.writeText(product.id.toString())}
                                                                >
                                                                    #{product.id}
                                                                </span>
                                                                <span className='related-product-row-supplier'>{product.supplierName}</span>
                                                                {product.supplierCategoryName && (
                                                                    <span className="related-product-row-supplier-category">
                                                                        {product.supplierCategoryName}
                                                                    </span>
                                                                )}
                                                            </span>
                                                            <span>
                                                                <span>
                                                                    <span className="related-product-row-field-label">Наличие:</span>
                                                                    <span className="related-product-row-stock-info">{product.stock || '-'}</span>
                                                                </span>
                                                                |
                                                                <span>
                                                                    <span className="related-product-row-field-label">Обновлено:</span>
                                                                    <span className="related-product-row-header-date">
                                                                        {product.updatedAt ? (
                                                                            <>
                                                                                {new Date(product.updatedAt).toLocaleDateString('ru-RU')}
                                                                                {' '}
                                                                                {new Date(product.updatedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                                                                            </>
                                                                        ) : '-'}
                                                                    </span>
                                                                </span>
                                                                |
                                                                <span>
                                                                    <span className="related-product-row-field-label">Доступно:</span>
                                                                    <span className="related-product-row-header-date">
                                                                        {product.availableDate ? (
                                                                            <>
                                                                                {(() => { const { date, time } = formatAvailableDate(product.availableDate, businessTimeZone); return `${date} ${time}`; })()}
                                                                            </>
                                                                        ) : '-'}
                                                                    </span>
                                                                </span>
                                                            </span>
                                                        </div>
                                                        <div className="related-product-row-body">
                                                            <div className="related-product-row-cell name-group-cell">
                                                                <div className="related-product-row-name" title={product.name}>
                                                                    {product.name}
                                                                </div>
                                                                <div className="related-product-row-tags">
                                                                    {product.sku ? (() => {
                                                                        const productKey = getProductKey(product.integrationId, product.productExternalId);
                                                                        const normalizedSku = product.sku.trim();
                                                                        const isExcluded = excludedSkusByProduct[productKey]?.has(normalizedSku) ?? false;
                                                                        return (
                                                                            <span className="related-product-row-sku-tag">
                                                                                <span className={`sku-tag-text ${isExcluded ? 'excluded' : ''}`}>
                                                                                    {product.sku}
                                                                                </span>
                                                                                {canEditProductRules && (
                                                                                    <button
                                                                                        type="button"
                                                                                        className={`barcode-toggle-btn ${isExcluded ? 'active' : ''}`}
                                                                                        title={isExcluded ? 'Включить обратно' : 'Исключить из слияний'}
                                                                                        onClick={() => handleToggleSkuExclusion(product, product.sku!)}
                                                                                    >
                                                                                        {isExcluded ? '↩' : '🚫'}
                                                                                    </button>
                                                                                )}
                                                                            </span>
                                                                        );
                                                                    })() : null}
                                                                    {(product.barcode?.split(",") ?? []).map(bc => bc.trim()).filter(Boolean).length > 0
                                                                        ? product.barcode!.split(",").map(bc => bc.trim()).filter(Boolean).map((bc, index) => {
                                                                            const productKey = getProductKey(product.integrationId, product.productExternalId);
                                                                            const isExcluded = excludedBarcodesByProduct[productKey]?.has(bc) ?? false;
                                                                            return (
                                                                                <span key={`${product.id}-${bc}-${index}`} className="barcode-tag-item">
                                                                                    <span className={isExcluded ? 'barcode-tag-text excluded' : 'barcode-tag-text'}>
                                                                                        {bc === selectedProductBarcode ? <b>{bc}</b> : bc}
                                                                                    </span>
                                                                                    {canEditProductRules && (
                                                                                        <button
                                                                                            type="button"
                                                                                            className={`barcode-toggle-btn ${isExcluded ? 'active' : ''}`}
                                                                                            title={isExcluded ? 'Включить обратно' : 'Исключить из слияний'}
                                                                                            onClick={() => handleToggleBarcodeExclusion(product, bc)}
                                                                                        >
                                                                                            {isExcluded ? '↩' : '🚫'}
                                                                                        </button>
                                                                                    )}
                                                                                </span>
                                                                            );
                                                                        })
                                                                        : null
                                                                    }
                                                                </div>
                                                            </div>
                                                            <div className='related-product-row-cell-prices'>
                                                                <div className="related-product-row-cell price1-cell">
                                                                    <span className="related-product-row-field-value price-value">{renderSourcePriceCell(product)}</span>
                                                                </div>
                                                                |
                                                                <div className="related-product-row-cell price2-cell">
                                                                    <span className="related-product-row-field-value price-value">{renderMarkupPriceCell(product.price, product.pricePercent, product.priceSum, product.categoryPricePercent, product.categoryPriceSum)}</span>
                                                                </div>
                                                                |
                                                                <div className="related-product-row-cell price3-cell">
                                                                    <span className="related-product-row-field-value price-value">{renderMarkupPriceCell(product.price2, product.pricePercent2, product.priceSum2, product.categoryPricePercent2, product.categoryPriceSum2)}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            </>
                                        )}
                                    </div>
                                        {activeDetailTab === 'attributes' && (
                                            <div className="product-attributes-detail">
                                                <div className="table-responsive">
                                                    <div className="products-table-container">
                                                        <table className="products-table product-attributes-table">
                                                            <thead className="products-table-header-sticky">
                                                                <tr>
                                                                    <th>Группа</th>
                                                                    <th>Атрибут</th>
                                                                    <th>Значение</th>
                                                                    <th>Ед.</th>
                                                                    <th>Тип</th>
                                                                    <th>Фильтр</th>
                                                                    <th>B2B</th>
                                                                    <th>Обновлено</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="products-table-body">
                                                                {loadingAttributes ? (
                                                                    <tr>
                                                                        <td colSpan={8} style={{ textAlign: 'center', padding: '1rem' }}>
                                                                            <div className="btn-spinner" />
                                                                            Загрузка атрибутов...
                                                                        </td>
                                                                    </tr>
                                                                ) : attributesError ? (
                                                                    <tr>
                                                                        <td colSpan={8} style={{ textAlign: 'center', padding: '1rem' }}>
                                                                            {attributesError}
                                                                        </td>
                                                                    </tr>
                                                                ) : mainProductAttributes.length === 0 ? (
                                                                    <tr>
                                                                        <td colSpan={8} style={{ textAlign: 'center', padding: '1rem' }}>
                                                                            Атрибуты не найдены
                                                                        </td>
                                                                    </tr>
                                                                ) : (
                                                                    mainProductAttributes.map(attribute => (
                                                                        <tr key={attribute.attributeDefinitionId}>
                                                                            <td>{attribute.groupName || '-'}</td>
                                                                            <td>
                                                                                <div className="attribute-name-cell">
                                                                                    <strong>{attribute.displayName || attribute.attributeName}</strong>
                                                                                    <span>{attribute.attributeCode}</span>
                                                                                </div>
                                                                            </td>
                                                                            <td>{attribute.value || '-'}</td>
                                                                            <td>{attribute.unitSymbol || attribute.unitName || '-'}</td>
                                                                            <td>{attribute.dataTypeName}</td>
                                                                            <td>{renderAttributeFlag(attribute.isFilterable)}</td>
                                                                            <td>{renderAttributeFlag(attribute.isExportedToB2B)}</td>
                                                                            <td>{formatAttributeUpdatedAt(attribute.updatedAt)}</td>
                                                                        </tr>
                                                                    ))
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {showCategoryMoveModal && (
            <div className="modal-backdrop-custom" onClick={() => setShowCategoryMoveModal(false)}>
                <div className="modal-dialog-custom animate-in category-move-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content-custom">
                        <div className="modal-header-custom">
                            <div>
                                <h4>Перемещение продуктов</h4>
                                <p>Выбрано продуктов: {selectedProductIds.size}</p>
                            </div>
                            <button className="btn-close" onClick={() => setShowCategoryMoveModal(false)}>✕</button>
                        </div>
                        <div className="modal-body-custom">
                            <div className="category-picker">
                                <input
                                    type="text"
                                    className="form-input-custom category-picker-search"
                                    placeholder="Фильтр по названию..."
                                    value={categorySearchFilter}
                                    onChange={(e) => setCategorySearchFilter(e.target.value)}
                                    autoFocus
                                />
                                <div className="category-picker-list">
                                    {filteredCategories.length === 0 ? (
                                        <div className="category-picker-empty">
                                            {categorySearchFilter ? 'Ничего не найдено' : 'Нет категорий'}
                                        </div>
                                    ) : (
                                        filteredCategories.map(cat => (
                                            <div
                                                key={cat.id}
                                                className={`category-picker-item ${cat.hasChildren ? 'disabled' : ''} ${targetCategoryId === cat.id ? 'selected' : ''}`}
                                                style={{ paddingLeft: `${12 + cat.depth * 20}px` }}
                                                onClick={cat.hasChildren ? undefined : () => setTargetCategoryId(cat.id)}
                                                title={cat.hasChildren ? 'Нельзя выбрать категорию с подкатегориями' : undefined}
                                            >
                                                <span className="category-picker-item-name">{cat.name}</span>
                                            </div>
                                        ))
                                    )}
                                </div>
                                <div className="category-picker-separator" />
                                <div
                                    className={`category-picker-item category-picker-abyss ${targetCategoryId === ABYSS_CATEGORY_ID ? 'selected' : ''}`}
                                    onClick={() => setTargetCategoryId(ABYSS_CATEGORY_ID)}
                                >
                                    🗑 Убрать из категории
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer-custom">
                            <button className="btn-secondary-custom" onClick={() => setShowCategoryMoveModal(false)} disabled={movingProducts}>
                                ✕ Отмена
                            </button>
                            <button 
                                className="btn-primary-custom" 
                                onClick={handleMoveToCategory} 
                                disabled={movingProducts || (!targetCategoryId && targetCategoryId !== ABYSS_CATEGORY_ID)}
                            >
                                {movingProducts ? <><span className="btn-spinner" /> Перемещение...</> : '✓ Переместить'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default ProductManagement;